import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthFacade } from '../facades/auth.facade';
import { toObservable } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs';

/**
 * Sends a student who has never declared a learning track to the one-time
 * chooser. Applied to the `student` branch in `app.routes.ts`, alongside
 * `authGuard` — the two are ordered so authentication is settled first.
 *
 * Only students are affected: a teacher or super_admin has no track, and
 * `needsLearningTrack()` is false for them, so this guard is transparent on
 * every other shell.
 *
 * New registrations never see the chooser — both signup forms write the column
 * inline. This exists for the students who predate it.
 */
export const learningTrackGuard: CanActivateFn = () => {
  const authFacade = inject(AuthFacade);
  const router = inject(Router);

  return toObservable(authFacade.isLoading).pipe(
    filter((isLoading) => !isLoading),
    map(() => {
      if (authFacade.needsLearningTrack()) {
        return router.createUrlTree(['/select-track']);
      }
      return true;
    }),
  );
};

/**
 * Guards `/select-track` itself: it is only for a signed-in student who still
 * owes the choice. Anyone else is dispatched to their own landing page, the
 * same way `onboardingGuard` does — otherwise a student who already chose could
 * reopen the page and hit the `lock_learning_track` trigger's `42501` instead of
 * a redirect.
 */
export const selectTrackGuard: CanActivateFn = () => {
  const authFacade = inject(AuthFacade);
  const router = inject(Router);

  return toObservable(authFacade.isLoading).pipe(
    filter((isLoading) => !isLoading),
    map(() => {
      if (!authFacade.isAuthenticated()) {
        return router.createUrlTree(['/auth/login']);
      }
      if (authFacade.isIncompleteStudent()) {
        return router.createUrlTree(['/onboarding']);
      }
      if (!authFacade.needsLearningTrack()) {
        if (authFacade.isSuperAdmin()) {
          return router.createUrlTree(['/admin']);
        }
        return router.createUrlTree([
          authFacade.isTeacher() ? '/teacher/home' : '/student/home',
        ]);
      }
      return true;
    }),
  );
};

/**
 * Blocks the live half of the student shell for a `recorded` student. Applied to
 * the `groups` child route in `student.routes.ts`, so the branch is unreachable
 * by direct URL and not merely hidden from the drawer.
 *
 * It redirects rather than returning `false`: a bare `false` leaves the router
 * on the previous URL with no feedback, which reads as a dead link.
 *
 * This is presentation, not security. Live content is still gated server-side —
 * `live_session_secrets` requires `access_tier = 'full'` **and** an active
 * enrolment in a group holding that session (Phase 7, D6).
 */
export const liveTrackGuard: CanActivateFn = () => {
  const authFacade = inject(AuthFacade);
  const router = inject(Router);

  return toObservable(authFacade.isLoading).pipe(
    filter((isLoading) => !isLoading),
    map(() => {
      if (authFacade.isRecordedOnlyStudent()) {
        return router.createUrlTree(['/student/home']);
      }
      return true;
    }),
  );
};
